import React, { useState } from 'react';
import { AttributeValue } from '../../../../api/protosCompiled/globalItem/globalItem_pb';
import { UB_AccountUser } from '../../../../api/protosCompiled/ub_users/ub_users_pb';
import { sortAndGroupAttributes } from './helper';
import {
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

interface AttributeValuesTableProps {
  attributeValues: AttributeValue.AsObject[];
  user: UB_AccountUser.AsObject | null;
  onEdit: (attributeValue: AttributeValue.AsObject | null) => void;
  onDelete: (attributeValueId: number) => void;
}

const AttributeValuesTable: React.FC<AttributeValuesTableProps> = ({
  attributeValues,
  user,
  onEdit,
  onDelete,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();

  const handleAddAttributeValue = () => {
    onEdit(null); // pass null to create a new one
  };

  const groupedEntries = Object.entries(sortAndGroupAttributes(attributeValues));

  const filteredEntries = groupedEntries.filter(([name]) =>
    name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        width: '100%',
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          p: 1,
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search attribute values..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: theme.palette.background.paper,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          aria-label="Search attribute values"
          inputProps={{
            'aria-label': 'Search attribute values input',
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: theme.palette.text.secondary }} />,
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleAddAttributeValue();
          }}
          aria-label="Add new attribute value"
          sx={{
            ml: 1,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          ADD
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f5f5f5',
        }}
      >
        <Table
          aria-label="Attribute values table"
          sx={{
            tableLayout: 'fixed',
            bgcolor: theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
            '& .MuiTableRow-root': {
              '&:nth-of-type(odd)': {
                bgcolor:
                  theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
              },
              '&:nth-of-type(even)': {
                bgcolor:
                  theme.palette.mode === 'dark' ? '#3c3c3c !important' : '#e0e0e0 !important',
              },
              '&:hover': {
                backgroundColor: `${
                  theme.palette.mode === 'dark'
                    ? 'rgba(33, 150, 243, 0.1)'
                    : 'rgba(33, 150, 243, 0.1)'
                } !important`,
              },
            },
            '& .MuiTableBody-root': {
              bgcolor: theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
            },
            '& .MuiTableCell-root': {
              padding: '8px 16px',
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
            },
            '& .MuiTableHead-root': {
              '& .MuiTableCell-root': {
                bgcolor:
                  theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
                color: theme.palette.text.primary,
                fontWeight: 600,
              },
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                bgcolor:
                  theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
              }}
            >
              <TableCell width="35%">Value Name</TableCell>
              <TableCell width="45%">Variables</TableCell>
              <TableCell width="20%" align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {user && filteredEntries.length > 0 ? (
              filteredEntries.map(([name, values], rowIndex) => {
                const uniqueRecords = Array.from(
                  new Map(values.map(v => [v.attributevalueid, v])).values()
                );

                return (
                  <TableRow
                    key={name}
                    hover
                    tabIndex={0}
                    aria-label={`Attribute value group: ${name}`}
                    sx={{
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? rowIndex % 2 === 0
                            ? '#2c2c2c !important'
                            : '#3c3c3c !important'
                          : rowIndex % 2 === 0
                            ? '#f5f5f5 !important'
                            : '#e0e0e0 !important',
                      '&.Mui-hover': {
                        bgcolor: `${
                          theme.palette.mode === 'dark'
                            ? 'rgba(33, 150, 243, 0.1)'
                            : 'rgba(33, 150, 243, 0.1)'
                        } !important`,
                      },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        bgcolor: 'inherit !important',
                        color: theme.palette.text.primary,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          color: theme.palette.text.primary,
                        }}
                      >
                        <span>{name}</span>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        bgcolor: 'inherit !important',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {uniqueRecords.map(attributeValue => (
                        <div
                          key={attributeValue.attributevalueid}
                          style={{
                            marginBottom: '8px',
                            color: theme.palette.text.primary,
                          }}
                        >
                          {attributeValue.attributevariablesList.map(v => (
                            <div key={v.itemattributevaluevariablemapid}>
                              {v.variable?.name || 'Unknown Variable'}: {v.variablevalue}
                            </div>
                          ))}
                        </div>
                      ))}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        bgcolor: 'inherit !important',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {uniqueRecords.map(attributeValue => (
                        <div key={attributeValue.attributevalueid} style={{ marginBottom: '8px' }}>
                          <Tooltip title="Edit attribute value">
                            <IconButton
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onEdit(attributeValue);
                              }}
                              aria-label={`Edit ${name}`}
                              size="small"
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  bgcolor: `rgba(33, 150, 243, 0.1)`,
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete attribute value">
                            <IconButton
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                onDelete(attributeValue.attributevalueid);
                              }}
                              aria-label={`Delete ${name}`}
                              size="small"
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  bgcolor: `rgba(244, 67, 54, 0.1)`,
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ))}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow
                sx={{
                  bgcolor:
                    theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
                }}
              >
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    bgcolor:
                      theme.palette.mode === 'dark' ? '#2c2c2c !important' : '#f5f5f5 !important',
                    color: theme.palette.text.secondary,
                  }}
                >
                  No attribute values found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};

export default React.memo(AttributeValuesTable);
